/**
 * API 클라이언트 - SPA에서 백엔드 API 호출
 *
 * 규율:
 * - Vite dev server proxy 경유 (/api -> localhost:8081, VITE_API_TARGET 로 오버라이드)
 * - 운영: nginx reverse proxy 또는 동일 도메인
 * - 기록 시각은 서버 API 응답을 그대로 사용 (Date.now() 금지 - GxP/ALCOA+)
 * - 인증 토큰은 Keycloak access_token (Authorization 헤더)
 */

import { getAccessToken } from "@/lib/auth";

const API_BASE_URL = import.meta.env.VITE_API_URL ?? "";

/**
 * 활성 회사(다회사) — 모든 요청에 X-Company-Id 헤더로 첨부(워크스페이스 슬러그와 동일 패턴, 전역 주입).
 * 서버는 멤버십을 검증한 뒤에만 해당 회사로 override 하므로(비멤버=홈 유지), 신뢰 경계는 서버임.
 * 탭 단위(sessionStorage)로 보관해 새로고침 후에도 활성 회사를 유지함.
 */
const ACTIVE_COMPANY_KEY = "eqms.activeCompanyId";
let currentCompanyId: string | null = null;

/** 활성 회사 설정(회사 스위처/생성 시 호출). null=홈 회사로 복귀. */
export function setActiveCompany(companyId: string | null): void {
  currentCompanyId = companyId;
  if (typeof window === "undefined") return;
  if (companyId) window.sessionStorage.setItem(ACTIVE_COMPANY_KEY, companyId);
  else window.sessionStorage.removeItem(ACTIVE_COMPANY_KEY);
}

/** 현재 활성 회사 ID(없으면 홈 회사=서버 기본). apiFetch 가 헤더 첨부 시 조회. */
export function getActiveCompany(): string | null {
  if (currentCompanyId === null && typeof window !== "undefined") {
    currentCompanyId = window.sessionStorage.getItem(ACTIVE_COMPANY_KEY);
  }
  return currentCompanyId;
}

/** 오류 보간값 — 문자열·숫자, 또는 다국어 라벨 맵(`{ko,en,..}`, 표시 시 현재 언어로 해석). */
export type ApiErrorParams = Record<string, string | number | Record<string, string>>;

/**
 * 정규화된 API 오류.
 *
 * @param params 번역 문구 `errors.<CODE>` 의 보간값 — 오류 본문의 숫자 최상위 멤버(예: 잠금 남은 초 `retryAfterSeconds`)와
 *   본문 `params` 확장 멤버(문자열·숫자·다국어 맵 `{ko,en,..}`)를 합친 것. 같은 이름이면 본문 `params` 가 우선.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly params: ApiErrorParams = {},
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** 동시편집 충돌 감지 헤더(서버 BaseVersionHeaders 와 같은 값). */
export const BASE_VERSION_HEADER = "X-Base-Version";
const BASE_ITEM_HEADER = "X-Base-Item";

/**
 * 화면이 본 기준 항목 — 서버가 잠금 아래 수정 번호를 비교해 다르면 409 ITEM_STALE.
 * id 생략 = 요청 대상 항목 자신. id 지정 = 하위 기록(예: 교육 참가자)의 부모.
 */
export interface BaseRef {
  id?: string;
  version: number;
}

interface FetchOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** 워크스페이스 컨텍스트 */
  workspaceSlug?: string;
  /** 기준 번호 검사(선택) — 없으면 서버가 검사하지 않음. */
  base?: BaseRef;
  /** 성공 응답의 헤더가 필요할 때(예: 작업 후 기준 번호 X-Base-Version). */
  onResponse?: (res: Response) => void;
}

/** 오류 본문의 숫자 최상위 멤버만 추림(번역 보간값). status 는 ApiError.status 로 따로 실으므로 제외. */
function numericMembers(body: unknown): Record<string, number> {
  if (typeof body !== "object" || body === null) return {};
  return Object.fromEntries(
    Object.entries(body).filter(
      (e): e is [string, number] => typeof e[1] === "number" && e[0] !== "status",
    ),
  );
}

/** 오류 본문 `params` 확장 멤버를 보간값으로 추림 — 문자열·숫자·문자열 맵만 허용(그 외 값은 버림). */
function bodyParams(body: unknown): ApiErrorParams {
  if (typeof body !== "object" || body === null) return {};
  const raw = (body as { params?: unknown }).params;
  if (typeof raw !== "object" || raw === null) return {};
  return Object.fromEntries(
    Object.entries(raw).flatMap(([k, v]): [string, ApiErrorParams[string]][] => {
      if (typeof v === "string" || typeof v === "number") return [[k, v]];
      if (typeof v === "object" && v !== null) {
        return [
          [
            k,
            Object.fromEntries(
              Object.entries(v).filter((e): e is [string, string] => typeof e[1] === "string"),
            ),
          ],
        ];
      }
      return [];
    }),
  );
}

/**
 * API fetch 래퍼
 * - 인증 헤더 자동 첨부 (Keycloak access_token)
 * - 에러 정규화
 * - TODO: 재시도 / token refresh / circuit breaker
 */
export async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { body, workspaceSlug, base, onResponse, ...init } = options;

  const accessToken = getAccessToken();

  const activeCompany = getActiveCompany();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...(workspaceSlug ? { "X-Workspace-Slug": workspaceSlug } : {}),
    // 활성 회사(다회사) — 서버가 멤버십 검증 후에만 override(비멤버=홈 유지). 신뢰 경계는 서버.
    ...(activeCompany ? { "X-Company-Id": activeCompany } : {}),
    // Keycloak access_token (있을 때만 첨부). 신뢰 경계는 서버 — UX 보조용.
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(base ? { [BASE_VERSION_HEADER]: String(base.version) } : {}),
    ...(base?.id ? { [BASE_ITEM_HEADER]: base.id } : {}),
    ...(init.headers as Record<string, string> | undefined),
  };

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    // Spring problem-detail(RFC 7807)은 detail/title 을, 커스텀 응답은 message 를 씀 — 사용자에게 서버 사유를 그대로 노출.
    const eb = errorBody as {
      code?: string;
      message?: string;
      detail?: string;
      title?: string;
    };
    throw new ApiError(
      response.status,
      eb.code ?? "UNKNOWN",
      eb.detail ?? eb.title ?? eb.message ?? `HTTP ${response.status}`,
      { ...numericMembers(errorBody), ...bodyParams(errorBody) },
    );
  }

  onResponse?.(response);

  // 본문이 없을 수 있는 응답(204 No Content, DELETE 등) → 빈 본문이면 JSON 파싱 생략.
  // response.json() 은 빈 본문에서 SyntaxError 를 던지므로 text 로 읽어 안전 파싱함.
  const text = await response.text();
  return text ? (JSON.parse(text) as T) : (undefined as T);
}

/**
 * 페이지네이션 응답 표준 형태
 * TODO: 백엔드 API 계약과 동기화
 */
export interface PagedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}
